import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useEmitter } from 'dashboard/composables/emitter';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { canAccessMassiva } from 'dashboard/helper/massivaAccess';
import { toast } from 'dashboard/helper/toast';
import AiNetworkOutagesAPI from 'dashboard/api/aiNetworkOutages';

// Massivas ABERTAS da conta pra barra superior (pedido do chamado de
// 20/08/2026: o sininho não serve porque toda ação cai lá). Lê o ciclo de
// vida já persistido (network_outages, Postgres puro) — nunca o compute ao
// vivo — e acompanha pelo MESMO evento realtime da página da Massiva
// (opened/updated/closed). Só pra quem tem a aba: admin ou papel com
// `massiva_access`, em conta com o Studio ligado — a mesma regra da sidebar.
const RELOAD_EVENTS = ['opened', 'updated', 'closed'];
// Teto da listagem (o controller clampa em 100). Acima disso o número vem do
// `total_count`; a cor sai da página, ordenada por severidade desc — a mais
// grave vem primeiro, então nunca mente pra menos.
const PER_PAGE = 100;
const BURST_MS = 500;

export function useMassivaAlert() {
  const { t } = useI18n();
  const currentAccount = useMapGetter('getCurrentAccount');
  const accountId = useMapGetter('getCurrentAccountId');
  const isFeatureEnabledonAccount = useMapGetter(
    'accounts/isFeatureEnabledonAccount'
  );

  const outages = ref([]);
  const totalCount = ref(0);
  const isLoading = ref(false);
  // Leitura falhando: avisa UMA vez por sequência de falhas (o realtime
  // dispara releitura a cada transição — numa queda de API durante uma
  // massiva seriam toasts empilhando em toda tela de todo admin) e o chip
  // mostra que o número é de antes, em vez de parecer atual.
  const isStale = ref(false);
  let burstTimer = null;
  let controller = null;

  const canSee = computed(() =>
    canAccessMassiva({
      permissions: currentAccount.value?.permissions,
      studioEnabled: isFeatureEnabledonAccount.value(
        accountId.value,
        FEATURE_FLAGS.AI
      ),
    })
  );

  const criticalCount = computed(
    () => outages.value.filter(o => o.severidade === 'critica').length
  );
  const isCritical = computed(() => criticalCount.value > 0);
  const isVisible = computed(() => canSee.value && totalCount.value > 0);

  const label = computed(() =>
    t('AI.MASSIVA.NAVBAR.LABEL', { count: totalCount.value }, totalCount.value)
  );
  const tooltip = computed(() => {
    const alerts = totalCount.value - criticalCount.value;
    const base = t('AI.MASSIVA.NAVBAR.TOOLTIP', {
      critical: criticalCount.value,
      alerts: Math.max(alerts, 0),
    });
    return isStale.value ? `${base} ${t('AI.MASSIVA.NAVBAR.STALE')}` : base;
  });

  const fetchOpen = async () => {
    if (!canSee.value) {
      outages.value = [];
      totalCount.value = 0;
      return;
    }
    controller?.abort();
    controller = new AbortController();
    isLoading.value = true;
    try {
      const { data } = await AiNetworkOutagesAPI.list(
        accountId.value,
        {
          status: 'open',
          perPage: PER_PAGE,
          sort: 'severidade',
          order: 'desc',
        },
        { signal: controller.signal }
      );
      outages.value = data.payload || [];
      totalCount.value = data.meta?.total_count ?? outages.value.length;
      isStale.value = false;
    } catch (error) {
      if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED')
        return;
      // Sem leitura não é "sem massiva": avisa (uma vez) em vez de sumir calado.
      if (!isStale.value) toast.error(t('AI.MASSIVA.NAVBAR.FETCH_ERROR'));
      isStale.value = true;
    } finally {
      isLoading.value = false;
    }
  };

  // Transição de massiva (opened/updated/closed) relê; o tick de retrato
  // (snapshot_written, ~2min × N instâncias) não muda o ciclo de vida.
  useEmitter(BUS_EVENTS.MASSIVA_EVENT, payload => {
    if (!RELOAD_EVENTS.includes(payload?.event_type)) return;
    clearTimeout(burstTimer);
    burstTimer = setTimeout(fetchOpen, BURST_MS);
  });

  watch([accountId, canSee], fetchOpen);
  onMounted(fetchOpen);
  onUnmounted(() => {
    clearTimeout(burstTimer);
    controller?.abort();
  });

  return {
    isVisible,
    isCritical,
    isLoading,
    isStale,
    label,
    tooltip,
    totalCount,
    criticalCount,
  };
}
